import { APP_NAME } from "@config/tracking-config";
import { parseStringBoolean } from "@lib/browser-utils";
import { default as LocalStorageCache } from "@lib/local-storage";
import { ServiceFactory } from "@lib/service-factory";
import {
  AccountService,
  AiCurriculumService,
  AppcuesService,
  ClassroomService,
  ConfigurationService,
  CourseService,
  DocumentsService,
  EventService,
  InsightsService,
  IntegrationService,
  MenuBuilderService,
  OrganizationService,
  OutcomesService,
  SetupChecklistService,
  SimulationsService,
  SnowplowTrackingService,
  TrekService,
  UserPreferencesService,
  UserService,
  xAPITrackingService,
} from "@services";

const caches = {
  currentUserCache: new LocalStorageCache("transfr:dashboard:currentUser"),
  tokenCache: new LocalStorageCache("transfr:dashboard:token"),
  appSettingsCache: new LocalStorageCache("transfr:dashboard:settings"),
  dateSettingsCache: new LocalStorageCache("transfr:dashboard:dateSettings"),
  proProfsCache: new LocalStorageCache("transfr:dashboard:proProfs"),
};

const toggles = {
  maintenanceWarningEnabled: parseStringBoolean(
    process.env.MAINTENANCE_WARNING_ENABLED
  ),
  maintenanceStartDate: process.env.MAINTENANCE_START_DATE,
  maintenanceEndDate: process.env.MAINTENANCE_END_DATE,
  maintenanceWindowInHours: process.env.MAINTENANCE_WINDOW_IN_HOURS,
  maintenanceWarningDisplayStartDate:
    process.env.MAINTENANCE_WARNING_DISPLAY_START_DATE,
  snowplowEnabled: parseStringBoolean(process.env.SNOWPLOW_ENABLED),
  learnerDashboardEnabled: parseStringBoolean(
    process.env.IS_LEARNER_DASHBOARD_ENABLED
  ),
  allowStudentPrereqToggle: parseStringBoolean(
    process.env.ALLOW_STUDENT_PREREQ_TOGGLE
  ),
  // Mission control flag added for testing/developing purposes
  missionControlEnabled: !!process.env.SOCKET_BRIDGE_URL,
  isIntegrationPreconfigEnabled: parseStringBoolean(
    process.env.IS_INTEGRATION_PRECONFIG_ENABLED
  ),
  isGrowthbookEnabled: parseStringBoolean(process.env.IS_GROWTHBOOK_ENABLED),
  isPreviewClassroomEnabled: parseStringBoolean(
    process.env.IS_PREVIEW_CLASROOM_ENABLED
  ),
  isMultiProductClassroomEnabled: parseStringBoolean(
    process.env.IS_MULTIPRODUCT_CLASSROOM_ENABLED
  ),
  isArchiveClassroomEnabled: parseStringBoolean(
    process.env.IS_ARCHIVE_CLASSROOM_ENABLED
  ),
  isArchiveUserEnabled: parseStringBoolean(process.env.IS_ARCHIVE_USER_ENABLED),
};

const factory = new ServiceFactory(caches);

const services = {
  accountService: factory.create(AccountService, {
    publicPaths: [
      new RegExp("^/auth/login/?$"),
      new RegExp("^/auth/reset/?$"),
      new RegExp("^/auth/reset/password/?$"),
      new RegExp("^/auth/reset/send/?$"),
      new RegExp("^/auth/claim/?$"),
    ],
  }),
  aiCurriculumService: factory.create(AiCurriculumService),
  eventService: factory.create(EventService, {
    publicPaths: [new RegExp("^/events/.*")],
  }),
  organizationService: factory.create(OrganizationService),
  classroomService: factory.create(ClassroomService),
  // The config-service is a sibling of the dashboard API (…/config-service).
  configurationService: factory.create(ConfigurationService, {
    baseUrl: process.env.DASHBOARD_API_URL?.replace(
      /\/api$/,
      "/config-service"
    ),
  }),
  courseService: factory.create(CourseService),
  userService: factory.create(UserService),
  simulationsService: factory.create(SimulationsService),
  menuBuilderService: factory.create(MenuBuilderService),
  integrationService: factory.create(IntegrationService),
  documentsService: factory.create(DocumentsService),
  insightsService: factory.create(InsightsService, {
    baseUrl: process.env.INSIGHTS_API_URL,
  }),
  trekService: factory.create(TrekService, {
    baseUrl: process.env.CESE_API_URL,
  }),
  outcomesService: factory.create(OutcomesService),
  setupChecklistService: factory.create(SetupChecklistService),
  userPreferencesService: factory.create(UserPreferencesService),
  appcuesService: factory.create(AppcuesService),
  snowplowTrackingService: new SnowplowTrackingService(
    APP_NAME,
    toggles.snowplowEnabled
  ),
  xApiTrackingService: new xAPITrackingService(),
};

/**
 * A container representing service and cache facilities
 * for our stores to consume
 * @typedef Container
 * @type {object}
 *
 * @property {LocalStorageCache} currentUserCache - A user cache for the logged in user
 * @property {LocalStorageCache} tokenCache - A JWT token cache used for our HttpClient
 * @property {LocalStorageCache} dateSettingsCache - A cache for the date settings
 * @property {AccountService} accountService - Account service
 * @property {AiCurriculumService} aiCurriculumService - AI Curriculum service
 * @property {OrganizationService} organizationService - Organization service
 * @property {UserService} userService - User service
 * @property {ClassroomService} classroomService - User service
 * @property {MenuBuilderService} menuBuilderService - Menu builder service
 * @property {TrekService} trekService - Trek service
 * @property {OutcomesService} outcomesService - Outcomes service
 * @property {SetupChecklistService} setupChecklistService - Setup checklist service
 * @property {UserPreferencesService} userPreferencesService - User preferences service
 * @property {AppcuesService} appcuesService - Appcues service
 * @property {SnowplowTrackingService} snowplowTrackingService - Snowplow tracking service
 * @property {xAPITrackingService} xApiTrackingService - xAPI tracking service
 *
 */
export default {
  ...caches,
  ...services,
  toggles,
};
